import { useEffect, useId, useMemo, useRef, useState } from "react";

type ImageCarouselLandingProps = {
  images: string[];
  showNoImagesText?: boolean;

  showArrows?: boolean;
  showDots?: boolean;

  heightClassName?: string;
  ariaLabel?: string;
};

const DRAG_THRESHOLD = 60;
const MAX_IMAGES = 5;

const ImageCarouselLanding: React.FC<ImageCarouselLandingProps> = ({
  images,
  showNoImagesText = false,

  showArrows = true,
  showDots = true,

  heightClassName = "h-[260px] sm:h-[320px] md:h-[550px]",
  ariaLabel = "Galería de imágenes",
}) => {
  const carouselId = useId();

  // 👇 limitar SIEMPRE a 5
  const safeImages = useMemo(() => images.slice(0, MAX_IMAGES), [images]);
  const total = safeImages.length;

  const [current, setCurrent] = useState(0);

  const [isDragging, setIsDragging] = useState(false);
  const startX = useRef(0);
  const deltaX = useRef(0);
  const draggedRef = useRef(false);

  useEffect(() => {
    if (current > total - 1) setCurrent(0);
  }, [total, current]);

  const goTo = (index: number) => {
    if (!total) return;
    const clamped = Math.max(0, Math.min(index, total - 1));
    setCurrent(clamped);
  };

  const next = () => goTo(current + 1);
  const prev = () => goTo(current - 1);

  // ===== drag =====
  const beginDrag = (clientX: number) => {
    if (total <= 1) return;
    setIsDragging(true);
    startX.current = clientX;
    deltaX.current = 0;
    draggedRef.current = false;
  };

  const moveDrag = (clientX: number) => {
    if (!isDragging) return;
    deltaX.current = clientX - startX.current;
    if (Math.abs(deltaX.current) > 8) draggedRef.current = true;
  };

  const endDrag = () => {
    if (!isDragging) return;
    setIsDragging(false);

    if (deltaX.current > DRAG_THRESHOLD) prev();
    if (deltaX.current < -DRAG_THRESHOLD) next();

    deltaX.current = 0;
  };

  // mouse
  const handleMouseDown = (e: React.MouseEvent) => beginDrag(e.clientX);
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    e.preventDefault();
    moveDrag(e.clientX);
  };
  const handleMouseUp = () => endDrag();

  // touch
  const handleTouchStart = (e: React.TouchEvent) =>
    beginDrag(e.touches[0].clientX);

  const handleTouchMove = (e: React.TouchEvent) =>
    moveDrag(e.touches[0].clientX);

  const handleTouchEnd = () => endDrag();

  if (!safeImages || safeImages.length === 0) {
    return showNoImagesText ? (
      <div className="text-center text-zinc-400 py-4">
        No hay imágenes disponibles.
      </div>
    ) : null;
  }

  return (
    <section className="w-full">
      <div
        id={carouselId}
        className="relative w-full overflow-hidden rounded-3xl border border-white/10 bg-black"
        role="region"
        aria-label={ariaLabel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={() => setIsDragging(false)}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* TRACK */}
        <div
          className={`flex w-full transition-transform duration-500 ease-in-out ${
            isDragging ? "cursor-grabbing" : "cursor-grab"
          }`}
          style={{
            transform: `translateX(-${current * 100}%)`,
          }}
        >
          {safeImages.map((img, i) => (
            <div
              key={i}
              className={`min-w-full w-full ${heightClassName} flex-shrink-0 relative`}
            >
              <img
                src={img}
                className="w-full h-full object-cover select-none pointer-events-none"
                draggable={false}
              />
              <div className="absolute inset-0 bg-black/20" />
            </div>
          ))}
        </div>

        {/* ARROWS */}
        {showArrows && total > 1 && (
          <>
            <button
              onClick={prev}
              className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 text-white"
            >
              ‹
            </button>

            <button
              onClick={next}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 text-white"
            >
              ›
            </button>
          </>
        )}

        {/* DOTS */}
        {showDots && total > 1 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
            {safeImages.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrent(i)}
                className={`h-2 rounded-full transition-all ${
                  current === i ? "w-6 bg-white" : "w-2 bg-white/40"
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default ImageCarouselLanding;